import { Vector4 } from "../../types/math_types.js";

interface Shelf
{
  y         : number;
  height    : number;
  current_x : number;
  layer     : number;
}

export interface IShelfAllocation
{
  /** Pixel X offset inside the atlas texture */
  px    : number;
  /** Pixel Y offset inside the atlas texture (top-down, Y=0 is top) */
  py    : number;
  /** Layer index inside TEXTURE_2D_ARRAY */
  pl    : number;
  /** UV rect in shader format: (u0, v0, du, dv)
   *  u0, v0 = top-left corner in [0..1] space
   *  du     = width  in [0..1] space (positive)
   *  dv     = height in [0..1] space (negative — goes top-down)
   */
  uv    : Vector4;
}

export interface IShelfFreeSlot extends IShelfAllocation
{
  w: number;
  h: number;
}

/**
 * Pure shelf-packing allocator.
 * Has zero WebGL dependencies — it only tracks where to place rectangles
 * inside an atlas of a given pixel size and layer count.
 *
 * The same instance can be shared by multiple WebGLDynamicAtlas objects
 * that target different WebGL contexts, so the placement logic stays in sync
 * while each context keeps its own GPU texture.
 */
export class WebGLShelfAllocator
{
  public readonly atlas_size  : number;
  public readonly layer_count : number;

  private shelves             : Shelf[] = [];
  private freed_slots         : IShelfFreeSlot[] = [];
  private active_allocations  : number = 0;
  private last_w              : number = 0;
  private last_h              : number = 0;

  constructor( atlas_size: number, layer_count: number )
  {
    this.atlas_size  = atlas_size;
    this.layer_count = layer_count;
    this.reset();
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Find or create a shelf slot for a rectangle of size (w × h).
   * Returns the allocation with pixel offsets and pre-calculated UV rect,
   * or null if the atlas is full.
   */
  public allocate( w: number, h: number, pad: number = 2 ): IShelfAllocation | null
  {
    const alloc_w = w + pad * 2;
    const alloc_h = h + pad * 2;

    if ( alloc_w > this.atlas_size || alloc_h > this.atlas_size )
    {
      console.error( "[WebGLShelfAllocator] Allocation failed: Request exceeds atlas dimensions." );
      return null;
    }

    let px = -1;
    let py = -1;
    let pl = -1;

    // 0. Try to reuse a freed slot
    for ( let i = 0; i < this.freed_slots.length; i++ )
    {
      const slot = this.freed_slots[i];
      if ( slot.w >= alloc_w && slot.h >= alloc_h )
      {
        const last = this.freed_slots.pop()!;
        if ( i < this.freed_slots.length )
          this.freed_slots[i] = last;

        this.active_allocations++;
        this.last_w = w;
        this.last_h = h;
        return { px: slot.px, py: slot.py, pl: slot.pl, uv: this.calcUV( slot.px, slot.py, w, h ) };
      }
    }

    // 1. Try to fit in an existing shelf
    for ( const shelf of this.shelves )
    {
      if ( shelf.height >= alloc_h && shelf.current_x + alloc_w <= this.atlas_size )
      {
        px = shelf.current_x;
        py = shelf.y;
        pl = shelf.layer;
        shelf.current_x += alloc_w;
        break;
      }
    }

    // 2. Open a new shelf on the current layer
    if ( px === -1 )
    {
      const last   = this.shelves[this.shelves.length - 1];
      const new_y  = last.y + last.height;

      if ( new_y + alloc_h <= this.atlas_size )
      {
        this.shelves.push( { y: new_y, height: alloc_h, current_x: alloc_w, layer: last.layer } );
        px = 0;
        py = new_y;
        pl = last.layer;
      }
      else
      {
        // 3. Current layer is full — try the next layer
        const next_layer = last.layer + 1;

        if ( next_layer < this.layer_count )
        {
          this.shelves.push( { y: 0, height: alloc_h, current_x: alloc_w, layer: next_layer } );
          px = 0;
          py = 0;
          pl = next_layer;
        }
        else
        {
          console.error( "[WebGLShelfAllocator] Atlas overflow: No more layers available for allocation." );
          return null;
        }
      }
    }

    this.active_allocations++;
    this.last_w = w;
    this.last_h = h;

    return { px: px + pad, py: py + pad, pl, uv: this.calcUV( px + pad, py + pad, w, h ) };
  }

  /**
   * Release one allocation. Adds it to the free list for slot reuse.
   * When the reference count reaches zero the allocator is fully reset.
   */
  public free( alloc: IShelfAllocation, w: number, h: number, pad: number = 2 ): void
  {
    if ( this.active_allocations <= 0 )
      return;

    this.freed_slots.push( { ...alloc, w: w + pad * 2, h: h + pad * 2 } );

    this.active_allocations--;
    if ( this.active_allocations === 0 )
      this.reset();
  }

  public getStats()
  {
    const last_shelf = this.shelves[this.shelves.length - 1];
    const used_h = last_shelf ? last_shelf.y + last_shelf.height : 0;
    const current_layer = last_shelf ? last_shelf.layer : 0;

    return {
      active: this.active_allocations,
      layer : current_layer + 1,
      total : this.layer_count,
      last_w: this.last_w,
      last_h: this.last_h,
      shelf_free_w: last_shelf ? this.atlas_size - last_shelf.current_x : this.atlas_size,
      layer_free_h: this.atlas_size - used_h,
      empty_layers: this.layer_count - ( current_layer + 1 ),
      size: this.atlas_size
    };
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  public reset(): void
  {
    this.shelves            = [ { y: 0, height: 0, current_x: 0, layer: 0 } ];
    this.freed_slots        = [];
    this.active_allocations = 0;
  }

  /**
   * Convert pixel offset + size to the UV rect format expected by shaders:
   *   x = u0  (left edge,  [0..1])
   *   y = v0  (top  edge,  [0..1]) — NOTE: WebGL V=0 is bottom, so we invert
   *   z = du  (width,      positive)
   *   w = dv  (height,     negative — advances top-down when multiplied by [0..1])
   */
  private calcUV( px: number, py: number, w: number, h: number ): Vector4
  {
    const s  = this.atlas_size;
    return {
      x: px / s,
      y: ( s - py ) / s,       // top-left in WebGL UV space
      z: w / s,
      w: -h / s,               // negative so uv.y + uv.w * t goes downward
    };
  }
}
